// "Upcoming payroll impact" — known future deductions from credit-service
// repayment schedules, grouped per payroll period. Everything shown is a
// SCHEDULED amount (payroll confirms at calculation time, e.g. min-net-pay
// clamping), and the section says so — a forecast is never presented as
// guaranteed. Hidden when there is nothing scheduled.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock } from 'lucide-react';
import { Badge } from '@/features/shared/components/ui/badge';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import type { UpcomingPeriod } from '../lib/upcoming';
import { formatMoney, humanizePeriod, MASK } from '../lib/format';

interface UpcomingDeductionsProps {
  periods: UpcomingPeriod[];
  loading: boolean;
  revealed: boolean;
}

export const UpcomingDeductions: React.FC<UpcomingDeductionsProps> = ({ periods, loading, revealed }) => {
  const { t } = useTranslation('payroll');

  if (loading) return <Skeleton className="h-32 w-full rounded-xl" />;
  if (periods.length === 0) return null;

  const money = (n: number, cur: string) => (revealed ? formatMoney(n, cur) : MASK);

  return (
    <section aria-labelledby="mypay-upcoming">
      <h2 id="mypay-upcoming" className="mb-1 flex items-center gap-2 text-h3 text-charcoal">
        <CalendarClock className="h-5 w-5 text-[#D9711E]" /> {t('myPay.upcomingTitle')}
      </h2>
      <p className="mb-3 text-xs text-muted-foreground">{t('myPay.upcomingNote')}</p>

      <ol className="space-y-3">
        {periods.map((p) => (
          <li key={p.period} className="rounded-xl border border-border p-4">
            <div className="flex items-center justify-between gap-2 border-b border-border pb-2">
              <span className="text-sm font-semibold text-charcoal">{humanizePeriod(p.period)}</span>
              {p.total != null && p.currency && (
                <span className="text-sm font-semibold tabular-nums text-charcoal">
                  −{money(p.total, p.currency)}
                </span>
              )}
            </div>
            <ul className="mt-2 space-y-1">
              {p.items.map((it, i) => (
                <li key={`${it.facilityId}-${i}`} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    {t('myPay.repaymentOf', { product: it.label })}
                    {it.final && <Badge variant="secondary" className="text-[10px]">{t('myPay.finalRepayment')}</Badge>}
                  </span>
                  <span className="tabular-nums text-charcoal">−{money(it.amountDue, it.currency)}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </section>
  );
};
